import { useCallback, useEffect, useState } from "react";
import { GetStaticProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";

import AdminStats from "../components/dashboard/AdminStats";
import AdvisorActivity from "../components/dashboard/AdvisorActivity";
import { DashboardData } from "./api/dashboard";
import { Icon } from "../components/Icon";
import Link from "next/link";
import Main from "../components/Main";
import PendingCalls from "../components/dashboard/PendingCalls";
import RecentActivity from "../components/dashboard/RecentActivity";
import RiskScoresList from "../components/dashboard/RiskScoresList";
import { Role } from "../types/enums";
import UnreadMessages from "../components/dashboard/UnreadMessages";
import { useSession } from "next-auth/react";

export default function Index() {
  const { data: session, status } = useSession();
  const { t } = useTranslation();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [myPatientsOnly, setMyPatientsOnly] = useState(false);

  const fetchDashboard = useCallback(async () => {
    if (!session || session.user.role === Role.Patient) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const url = myPatientsOnly
        ? "/api/dashboard?myPatients=true"
        : "/api/dashboard";
      const response = await fetch(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Dashboard API error:", response.status, errorData);
        throw new Error(
          errorData.message ||
            `Failed to fetch dashboard data (${response.status})`
        );
      }
      const data = await response.json();
      setDashboardData(data);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [session, myPatientsOnly]);

  useEffect(() => {
    if (status === "authenticated") {
      fetchDashboard();
    } else if (status === "unauthenticated") {
      setLoading(false);
    }
  }, [status, fetchDashboard]);

  const isAdmin = session?.user.role === Role.Admin;
  const isAdvisorOrAdmin =
    session?.user.role === Role.Advisor || session?.user.role === Role.Admin;

  // Patient view - keep the existing welcome content
  if (session?.user.role === Role.Patient) {
    return (
      <Main title={t("dashboard.welcomeTitle")}>
        <p>{t("dashboard.welcomeText1")}</p>
        <p>{t("dashboard.welcomeText2")}</p>
        <p>{t("dashboard.welcomeText3")}</p>
        <Link href="/articles" className="btn btn-primary">
          {t("dashboard.seeYourContent")}
          <Icon name="FileText" size="s" />
        </Link>
      </Main>
    );
  }

  // Advisor/Admin dashboard view
  return (
    <Main title={t("dashboard.title")}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">{t("dashboard.overview")}</h4>
        <div className="d-flex gap-2">
          <button
            className={`btn btn-sm ${
              myPatientsOnly ? "btn-primary" : "btn-outline-primary"
            }`}
            onClick={() => setMyPatientsOnly(!myPatientsOnly)}
            disabled={loading}
          >
            {myPatientsOnly ? "✓ " : ""}{t("myPatients")}
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={fetchDashboard}
            disabled={loading}
          >
            <Icon name="RefreshCw" size="s" />
            {t("refresh")}
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t("loading")}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
          <button className="btn btn-link" onClick={fetchDashboard}>
            {t("tryAgain")}
          </button>
        </div>
      )}

      {!loading && !error && dashboardData && isAdvisorOrAdmin && (
        <>
          {/* Top row - Patient counts by tier */}
          <div className="row g-3 mb-3">
            <div className="col-6 col-md-4 col-lg-2">
              <div className="card h-100">
                <div className="card-body text-center">
                  <div className="fs-2 fw-bold text-secondary">
                    {dashboardData.patientCounts.FREE}
                  </div>
                  <small className="text-muted">{t("dashboard.freePatients")}</small>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <div className="card h-100">
                <div className="card-body text-center">
                  <div className="fs-2 fw-bold text-primary">
                    {dashboardData.patientCounts.BASIC}
                  </div>
                  <small className="text-muted">{t("dashboard.basicPatients")}</small>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <div className="card h-100">
                <div className="card-body text-center">
                  <div className="fs-2 fw-bold text-success">
                    {dashboardData.patientCounts.PREMIUM}
                  </div>
                  <small className="text-muted">{t("dashboard.premiumPatients")}</small>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <div className="card h-100">
                <div className="card-body text-center">
                  <div className="fs-2 fw-bold text-info">
                    {dashboardData.patientCounts.PLUS}
                  </div>
                  <small className="text-muted">{t("dashboard.plusPatients")}</small>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <div className="card h-100">
                <div className="card-body text-center">
                  <div className="fs-2 fw-bold text-warning">
                    {dashboardData.patientCounts.PRO}
                  </div>
                  <small className="text-muted">{t("dashboard.proPatients")}</small>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <div className="card h-100">
                <div className="card-body text-center">
                  <div className="fs-2 fw-bold text-dark">
                    {dashboardData.patientCounts.MENOPAUSE}
                  </div>
                  <small className="text-muted">{t("dashboard.menopausePatients")}</small>
                </div>
              </div>
            </div>
          </div>

          {/* Second row - Messages and Calls */}
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <UnreadMessages messages={dashboardData.unreadMessages} />
            </div>
            <div className="col-md-6">
              <PendingCalls calls={dashboardData.pendingCalls} />
            </div>
          </div>

          {/* Third row - Advisor Activity and User Activity */}
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <AdvisorActivity activities={dashboardData.advisorActivity} />
            </div>
            <div className="col-md-6">
              <RecentActivity activities={dashboardData.recentActivity} />
            </div>
          </div>

          {/* Fourth row - Risk Scores */}
          <div className="row g-3 mb-3">
            <div className="col-12">
              <RiskScoresList riskScores={dashboardData.riskScores} />
            </div>
          </div>

          {/* Admin-only section */}
          {isAdmin &&
            dashboardData.newSignups &&
            dashboardData.subscriptionSummary &&
            dashboardData.systemStats && (
              <AdminStats
                newSignups={dashboardData.newSignups}
                subscriptionSummary={dashboardData.subscriptionSummary}
                systemStats={dashboardData.systemStats}
              />
            )}
        </>
      )}

      {!loading && !error && !dashboardData && !isAdvisorOrAdmin && (
        <div className="alert alert-info">
          {t("dashboard.pleaseLogin")}
        </div>
      )}
    </Main>
  );
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale ?? "en", ["common"])),
    },
  };
}
